import { useEffect, useState } from "react";
import { PageHeader } from "../../components/common/PageHeader";
import { DataTable } from "../../components/common/DataTable";
import { StatusBadge } from "../../components/common/StatusBadge";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { CorrectionsService } from "../../services";
import { dateTime } from "../../utils/format";
import { businessDateTimeValue } from "../../utils/sriLankaTime";
import { businessDayRange, businessRange, currentBusinessDate } from "../../utils/businessDay";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { useCan } from "../../hooks/useCan";

export const CorrectionsAdminPage = () => {
  const can = useCan();
  const initialBusinessDate = currentBusinessDate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null); // {mode: 'approve'|'reject', req}
  const [busy, setBusy] = useState(false);
  const [filterDraft, setFilterDraft] = useState({
    search: "",
    outlet: "ALL",
    mode: "single",
    businessDate: initialBusinessDate,
    fromDate: initialBusinessDate,
    toDate: initialBusinessDate,
  });
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    search: "",
    outlet: "ALL",
    ...businessDayRange(initialBusinessDate),
  }));
  const [filterRevision, setFilterRevision] = useState(0);
  const [filterError, setFilterError] = useState("");

  const load = () => {
  setLoading(true);
  CorrectionsService.list()
    .then((data) => setRows([...data].sort((a, b) => businessDateTimeValue(b.createdAt) - businessDateTimeValue(a.createdAt))))
    .finally(() => setLoading(false));
};
  useEffect(() => { load(); }, []);

  const act = async () => {
    setBusy(true);
    try {
      if (confirm.mode === "approve") await CorrectionsService.approve(confirm.req.requestId);
      else await CorrectionsService.reject(confirm.req.requestId);
      toast.success(confirm.mode === "approve" ? "Approved" : "Rejected");
      setConfirm(null); load();
    } catch (e) { toast.error(e?.response?.data?.message || "Failed"); }
    finally { setBusy(false); }
  };

  const updateFilterDraft = (field, value) => {
    setFilterDraft((current) => ({ ...current, [field]: value }));
  };

  const updateOutletFilter = (value) => {
    setFilterDraft((current) => ({ ...current, outlet: value }));
    setAppliedFilters((current) => ({ ...current, outlet: value }));
    setFilterRevision((revision) => revision + 1);
  };

  const applyFilters = () => {
    let dateRange;
    if (filterDraft.mode === "single") {
      if (!filterDraft.businessDate) {
        setFilterError("Select a date before applying filters.");
        return;
      }
      dateRange = businessDayRange(filterDraft.businessDate);
    } else {
      if (!filterDraft.fromDate || !filterDraft.toDate) {
        setFilterError("Select both a From Date and a To Date.");
        return;
      }
      if (filterDraft.fromDate > filterDraft.toDate) {
        setFilterError("From Date cannot be after To Date.");
        return;
      }
      dateRange = businessRange(filterDraft.fromDate, filterDraft.toDate);
    }

    setFilterError("");
    setAppliedFilters((current) => ({
      ...current,
      search: filterDraft.search,
      ...dateRange,
    }));
    setFilterRevision((revision) => revision + 1);
  };

  const resetFilters = () => {
    const today = currentBusinessDate();
    const reset = {
      search: "",
      outlet: "ALL",
      mode: "single",
      businessDate: today,
      fromDate: today,
      toDate: today,
    };
    setFilterDraft(reset);
    setAppliedFilters({ search: "", outlet: "ALL", ...businessDayRange(today) });
    setFilterError("");
    setFilterRevision((revision) => revision + 1);
  };

  const searchNeedle = appliedFilters.search.trim().toLowerCase();
  const filteredRows = rows.filter((request) => {
    const matchesOutlet =
      appliedFilters.outlet === "ALL" || request.outletCode === appliedFilters.outlet;
    const billText = `${request.saleCode || ""} ${request.billNumber || ""}`.toLowerCase();
    const matchesSearch = !searchNeedle || billText.includes(searchNeedle);
    // Backend LocalDateTime values and shared business-day bounds are ISO
    // wall-time strings, so lexical comparison avoids browser timezone shifts.
    const createdAt = String(request.createdAt || "");
    const matchesDate =
      createdAt >= appliedFilters.fromDateTime &&
      createdAt < appliedFilters.toDateTime;
    return matchesOutlet && matchesSearch && matchesDate;
  });

  return (
    <div>
      <PageHeader title="Correction Requests" subtitle="Approving a correction cancels the sale and restores stock to original batches." />
      <div className="mb-4 flex flex-nowrap items-center gap-2 overflow-x-auto">
        <Input
          aria-label="Search correction requests"
          placeholder="Search bill no..."
          value={filterDraft.search}
          onChange={(event) => updateFilterDraft("search", event.target.value)}
          className="w-52 shrink-0"
          data-testid="correction-search"
        />
        <Select value={filterDraft.outlet} onValueChange={updateOutletFilter}>
          <SelectTrigger
            className="w-40 shrink-0"
            data-testid="correction-outlet-filter"
            aria-label="Filter correction requests by outlet"
          >
            <SelectValue />
          </SelectTrigger>

          <SelectContent>
            <SelectItem value="ALL">All</SelectItem>
            <SelectItem value="BAR">BAR</SelectItem>
            <SelectItem value="RESTAURANT">RESTAURANT</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex shrink-0 rounded-lg bg-slate-100 p-1">
          <button
            type="button"
            onClick={() => {
              setFilterDraft((current) => ({ ...current, mode: "single" }));
              setFilterRevision((revision) => revision + 1);
            }}
            className={`rounded-md px-3 py-1.5 text-sm ${filterDraft.mode === "single" ? "bg-white text-navy-800 shadow-sm" : "text-slate-600"}`}
            aria-pressed={filterDraft.mode === "single"}
          >
            Single Date
          </button>
          <button
            type="button"
            onClick={() => {
              setFilterDraft((current) => ({ ...current, mode: "range" }));
              setFilterRevision((revision) => revision + 1);
            }}
            className={`rounded-md px-3 py-1.5 text-sm ${filterDraft.mode === "range" ? "bg-white text-navy-800 shadow-sm" : "text-slate-600"}`}
            aria-pressed={filterDraft.mode === "range"}
          >
            Date Range
          </button>
        </div>
        {filterDraft.mode === "single" ? (
          <Input
            type="date"
            aria-label="Correction request business date"
            value={filterDraft.businessDate}
            onChange={(event) => updateFilterDraft("businessDate", event.target.value)}
            className="w-40 shrink-0"
            data-testid="correction-business-date"
          />
        ) : (
          <>
            <Input
              type="date"
              aria-label="Correction request from business date"
              value={filterDraft.fromDate}
              onChange={(event) => updateFilterDraft("fromDate", event.target.value)}
              className="w-40 shrink-0"
              data-testid="correction-from-date"
            />
            <Input
              type="date"
              aria-label="Correction request to business date"
              value={filterDraft.toDate}
              onChange={(event) => updateFilterDraft("toDate", event.target.value)}
              className="w-40 shrink-0"
              data-testid="correction-to-date"
            />
          </>
        )}
        <Button type="button" onClick={applyFilters} className="shrink-0" data-testid="correction-apply-filters">
          Apply
        </Button>
        <Button type="button" variant="outline" onClick={resetFilters} className="shrink-0" data-testid="correction-reset-filters">
          Reset
        </Button>
      </div>
      {filterError && <div className="mb-3 text-sm text-rose-600" role="alert">{filterError}</div>}
      <DataTable
        paginationResetKey={filterRevision}
        loading={loading}
        rows={filteredRows.map((r) => ({ ...r, key: r.requestId }))}
        empty="No correction requests."
        testId="corrections-table"
        columns={[
          { key: "saleCode", header: "Sale", render: (r) => r.saleCode || r.billNumber || `Sale #${r.saleId}` },
          {
            key: "outletCode",
            header: "Outlet",
            render: (r) => {
              const outletLabel = r.outletCode === "BAR"
                ? "Bar"
                : r.outletCode === "RESTAURANT"
                  ? "Restaurant"
                  : null;

              return outletLabel ? (
                <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-700">
                  {outletLabel}
                </span>
              ) : "—";
            },
          },
          { key: "requestedByName", header: "Requested by", render: (r) => r.requestedByName || r.requestedByFullName || r.requestedByUsername || "—" },
          { key: "reason", header: "Reason" },
          { key: "status", header: "Status", render: (r) => <StatusBadge value={r.status} /> },
          { key: "createdAt", header: "When", render: (r) => dateTime(r.createdAt) },
          {
            key: "actions", header: "", render: (r) => (r.status === "PENDING" && can.approveCorrection) ? (
              <div className="flex gap-2">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setConfirm({ mode: "approve", req: r }); }} data-testid={`approve-${r.requestId}`}><Check className="h-3.5 w-3.5 mr-1" /> Approve</Button>
                <Button size="sm" variant="outline" onClick={() => { setConfirm({ mode: "reject", req: r }); }} data-testid={`reject-${r.requestId}`}><X className="h-3.5 w-3.5 mr-1" /> Reject</Button>
              </div>
            ) : <span className="text-slate-400 text-xs">—</span>
          },
        ]}
      />

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(v) => !v && setConfirm(null)}
        title={confirm?.mode === "approve" ? "Approve correction?" : "Reject correction?"}
        description={confirm?.mode === "approve"
          ? "Approving will set the sale to CANCELLED and restore stock to the original batches."
          : "Rejecting will keep the sale as completed."}
        confirmText={confirm?.mode === "approve" ? "Approve" : "Reject"}
        danger={confirm?.mode === "approve"}
        loading={busy}
        onConfirm={act}
      >
      </ConfirmDialog>
    </div>
  );
};
